import { useState } from 'react';
import { Box, Chip } from '@mui/material';
import PropTypes from 'prop-types';

import { Typography, Collapse, IconButton } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExperienceWalletDisplayItem from './ExperienceWalletDiplayItem';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import VerifiedIcon from '@mui/icons-material/Verified';

/* i18n */
import { useIntl } from 'react-intl';
import ExperienceWalletForm from './ExperienceWalletForm';

/* Icons */
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PlaylistAddCheckCircleOutlinedIcon from '@mui/icons-material/PlaylistAddCheckCircleOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import ClassOutlinedIcon from '@mui/icons-material/ClassOutlined';
import CardMembershipOutlinedIcon from '@mui/icons-material/CardMembershipOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import LocalLibraryOutlinedIcon from '@mui/icons-material/LocalLibraryOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import WorkIcon from '@mui/icons-material/Work';
import { RANKS_BY_BRANCH } from 'constants/militaryOptions';
import { EDUCATION_LEVEL_BY_ID, EDUCATION_CREDENTIAL_LABEL_IDS } from 'constants/educationLevelOptions';
import { jstRankOptions } from 'components/JSTcomponents/jstSectionsConfig';

const ALL_RANKS = Object.values(RANKS_BY_BRANCH).flat();

export function getRankLabel(experience) {
  if (experience.type !== 'militaryExperience' || !experience.rank) return null;
  if (experience.isJst) {
    const match = jstRankOptions.find((r) => r.value === experience.rank);
    return match?.label || experience.rank;
  }
  const branchRanks = experience.branch ? RANKS_BY_BRANCH[experience.branch] : null;
  const match = branchRanks
    ? branchRanks.find((r) => r.value === experience.rank)
    : ALL_RANKS.find((r) => r.value === experience.rank);
  return match?.label || experience.rank;
}

export const typeIconDict = {
  apprenticeship: PeopleAltOutlinedIcon,
  assessment: PlaylistAddCheckCircleOutlinedIcon,
  badge: VerifiedOutlinedIcon,
  course: ClassOutlinedIcon,
  certification: CardMembershipOutlinedIcon,
  degree: SchoolOutlinedIcon,
  employment: MeetingRoomOutlinedIcon,
  internship: LocalLibraryOutlinedIcon,
  license: BadgeOutlinedIcon,
  militaryCourse: MilitaryTechIcon,
};

export const sourceTypeIconDict = {
  work: WorkIcon,
  education: SchoolOutlinedIcon,
  military: MilitaryTechIcon,
};

export const typeDict = {
  assessment: 'experience.form.assessmentType',
  badge: 'experience.form.badgeType',
  // Backend CredentialTypeEnum.openbadge covers both LER "OpenBadgeCredential" and
  // "AchievementCredential" types — both are badges to the user.
  openbadge: 'experience.form.badgeType',
  // Defensive: in case the backend ever sends this raw/unnormalized instead of openbadge.
  achievement: 'experience.form.achievementType',
  AchievementCredential: 'experience.form.achievementType',
  course: 'experience.form.courseType',
  degree: 'experience.form.educationDegreeType',
  license: 'experience.form.licenseType',
  certification: 'experience.form.certificationType',
  internship: 'experience.form.internshipType',
  employment: 'experience.form.employmentType',
  militaryCourse: 'experience.form.militaryCourseType',
  militaryExperience: 'experience.form.militaryExperienceType',
  apperenticeshipRTI: 'experience.form.apprenticeshipRTIType',
  otherLearning: 'experience.form.otherLearningType',
  academicCourse: 'experience.form.academicCourseType',
  academicDegree: 'experience.form.academicDegreeType',
  'degree|High School Diploma or Equivalent (GED)': 'experience.form.degreeLevel.highSchoolDiplomaOrGED',
  'degree|Some College-No Credential': 'experience.form.degreeLevel.someCollegeNoCredential',
  'degree|Certificate of Proficiency': 'experience.form.degreeLevel.certificateOfProficiency',
  'degree|Technical Certificate': 'experience.form.degreeLevel.technicalCertificate',
  "degree|Associate's Degree": 'experience.form.degreeLevel.associatesDegree',
  'degree|Advanced Certificate': 'experience.form.degreeLevel.advancedCertificate',
  "degree|Bachelor's Degree": 'experience.form.degreeLevel.bachelorsDegree',
  "degree|Post Bachelor's Certificate": 'experience.form.degreeLevel.postBachelorsCertificate',
  "degree|Master's Degree": 'experience.form.degreeLevel.mastersDegree',
  'degree|Graduate Degree': 'experience.form.degreeLevel.graduateDegree',
  'degree|Doctorate-Professional Practice (MD, JD, etc)': 'experience.form.degreeLevel.doctorateProfessionalPractice',
  'degree|Doctorate-Research/Scholarly (PhD, EdD, etc)': 'experience.form.degreeLevel.doctorateResearchScholarly',
};

export function getTypeLabelId(experience) {
  if (experience.type === 'apprenticeship') {
    return experience.sourceType === 'work'
      ? 'experience.form.apprenticeshipType'
      : 'experience.form.apprenticeshipRTIType';
  }
  // JST academic degrees carry the credential enum as their type, so fall back to
  // the shared credential list rather than showing the raw value.
  return typeDict[experience.type] ?? EDUCATION_CREDENTIAL_LABEL_IDS[experience.type] ?? null;
}

// Labels for LCW/DCC credential types that don't map to typeDict i18n keys
export const lcwTypeLabels = {
  'CertificationV1.1': 'Certification',
  'CourseCompletionV1.1': 'Course Completion',
  VerifiableCredential: 'Credential',
};

export default function ExperienceWalletDrawer({
  experience,
  experiences,
  currentIndex,
  replaceItem,
  deleteItem,
  saveMilitaryCourses,
  saveAcademicDegrees,
}) {
  const intl = useIntl();
  const [expanded, setExpanded] = useState(false);
  const [currentContent, setCurrentContent] = useState('display');

  const RowIcon = typeIconDict[experience.type] || sourceTypeIconDict[experience.sourceType] || WorkIcon;

  // For degree items, show the specific education level (e.g. "Graduate Certificate")
  // resolved from the stored numeric id, instead of the generic "Education Degree" label.
  const degreeLevelOption =
    experience.type === 'degree' && experience.level != null
      ? EDUCATION_LEVEL_BY_ID.get(Number(experience.level))
      : null;

  return (
    <Box sx={{ mb: 1 }}>
      <Box
        sx={{
          boxSizing: 'border-box',
          borderRadius: 2,
          width: '100%',
          border: (theme) => `solid 1px ${theme.palette.grey[500]}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          px: 2,
          pt: 1,
          pb: experience.wallet ? 1 : 1,
          position: 'relative',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mr: 0.5, mt: '14px' }}>
          <RowIcon sx={{ color: 'secondary.main', fontSize: 24 }} />
          {experience.wallet ? (
            <Chip
              icon={<VerifiedIcon sx={{ fontSize: '10px !important' }} />}
              label="Verified"
              size="small"
              sx={{
                mt: 0.5,
                backgroundColor: '#e8f5e9',
                color: '#2e7d32',
                fontWeight: 700,
                fontSize: '0.6rem',
                height: 16,
                '& .MuiChip-label': { px: 0.5 },
              }}
            />
          ) : null}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            textOverflow: 'ellipsis',
            whiteSpace: expanded ? null : 'nowrap',
            overflow: 'hidden',
          }}
        >
          <Typography
            variant="h3"
            component="h2"
            sx={{
              fontWeight: 700,
              ml: 1,
              mr: 2,
              fontSize: 16,
              textOverflow: expanded ? 'initial' : 'ellipsis',
              whiteSpace: expanded ? 'initial' : 'nowrap',
              overflow: 'hidden',
              position: 'relative',
              top: 6,
            }}
          >
            {getRankLabel(experience) ||
              (degreeLevelOption
                ? intl.formatMessage({ id: degreeLevelOption.labelId })
                : getTypeLabelId(experience)
                ? intl.formatMessage({ id: getTypeLabelId(experience) })
                : lcwTypeLabels[experience.type] ?? experience.type)}{' '}
            {experience.wallet ? (
              <AccountBalanceWalletIcon sx={{ fontSize: 16, position: 'relative', top: 3, color: 'primary.dark' }} />
            ) : null}{' '}
          </Typography>
          {experience.wallet ? (
            <Typography sx={{ ml: 1, color: 'primary.main' }} variant="caption">
              {intl.formatMessage({ id: 'experience.fromWallet' })}
            </Typography>
          ) : null}
          <Typography
            variant="h3"
            component="h2"
            sx={{
              ml: 1,
              mr: 2,
              fontSize: 16,
              transition: 'all ease 0.5s',
              textOverflow: expanded ? 'initial' : 'ellipsis',
              whiteSpace: expanded ? 'initial' : 'nowrap',
              overflow: 'hidden',
            }}
          >
            {experience.title}
          </Typography>
        </Box>

        <Box
          sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', paddingTop: '6px' }}
        >
          <Chip
            sx={{ marginTop: '5px' }}
            label={`${experience.skills?.length ?? '0'} ${intl.formatMessage({
              id: experience.skills?.length === 1 ? 'experience.drawer.skillSingular' : 'experience.drawer.skillPlural',
            })}`}
          />
          <IconButton
            color="secondary"
            style={{ padding: 8 }}
            title={intl.formatMessage({ id: 'accessibility.expandButton' })}
            size="large"
          >
            {expanded ? <KeyboardArrowUpIcon sx={{ fontSize: 26 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 26 }} />}
          </IconButton>
        </Box>
      </Box>
      <Collapse
        in={expanded}
        sx={{
          border: (theme) => `solid 1px ${theme.palette.grey[500]}`,
          borderRadius: 2,
          px: 2,
        }}
      >
        {currentContent === 'display' ? (
          <ExperienceWalletDisplayItem
            experience={experience}
            setCurrentContent={setCurrentContent}
            deleteItem={deleteItem}
            currentIndex={currentIndex}
          />
        ) : (
          <ExperienceWalletForm
            formScope={experience.sourceType || 'work'}
            experience={experience}
            setCurrentContent={setCurrentContent}
            currentIndex={currentIndex}
            replaceItem={replaceItem}
            experiences={experiences.filter((exp) => !exp.isJst && !exp.isMilitaryCourse && !exp.isAcademicDegree)}
            saveMilitaryCourses={saveMilitaryCourses}
            saveAcademicDegrees={saveAcademicDegrees}
            showMilitaryCourse={experience.isMilitaryCourse || experience.isAcademicDegree || false}
          />
        )}
      </Collapse>
    </Box>
  );
}
ExperienceWalletDrawer.propTypes = {
  currentIndex: PropTypes.number,
  replaceItem: PropTypes.func,
  deleteItem: PropTypes.func,
  saveMilitaryCourses: PropTypes.func,
  saveAcademicDegrees: PropTypes.func,
  experiences: PropTypes.array,
  experience: PropTypes.shape({
    type: PropTypes.string,
    level: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sourceType: PropTypes.string,
    isMilitaryCourse: PropTypes.bool,
    isAcademicDegree: PropTypes.bool,
    fields: PropTypes.array,
    title: PropTypes.string,
    endDate: PropTypes.string,
    description: PropTypes.string,
    entity: PropTypes.string,
    wallet: PropTypes.bool,
    skills: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        slice: PropTypes.func,
      })
    ),
  }),
};
